import { useCallback, useEffect, useMemo, useState, type FormEvent } from "react";
import * as endpoints from "@/api/endpoints";
import type { CatalogProduct, CatalogResponse, Plan } from "@/api/types";
import { apiErrorMessage } from "@/api/client";
import { useCart } from "@/cart/CartContext";
import { PlanChangeDialog } from "@/components/PlanChangeDialog";
import { StockBadge } from "@/components/StockBadge";
import { formatYen } from "@/utils/format";

const PLAN_LABEL: Record<Plan, string> = {
  light: "Light",
  standard: "Standard",
  premium: "Premium",
};

type ViewMode = "grid" | "list";
const VIEW_MODE_STORAGE_KEY = "kz.catalog.view_mode";
const INCLUDE_OOS_STORAGE_KEY = "kz.catalog.include_out_of_stock";

// 商品リスト CSV ダウンロードボタンの表示フラグ。
// 一時停止中 (2026-07-07 Kota さん判断)。再開時は true に戻す +
// backend の .env から CATALOG_CSV_EXPORT_ENABLED=false を削除する。
const CSV_EXPORT_ENABLED = false;

function loadViewMode(): ViewMode {
  try {
    const raw = localStorage.getItem(VIEW_MODE_STORAGE_KEY);
    if (raw === "list" || raw === "grid") return raw;
  } catch {
    // ignore storage errors
  }
  return "grid";
}

function saveViewMode(mode: ViewMode) {
  try {
    localStorage.setItem(VIEW_MODE_STORAGE_KEY, mode);
  } catch {
    // ignore
  }
}

function loadIncludeOutOfStock(): boolean {
  try {
    return localStorage.getItem(INCLUDE_OOS_STORAGE_KEY) === "1";
  } catch {
    return false;
  }
}

function saveIncludeOutOfStock(value: boolean) {
  try {
    localStorage.setItem(INCLUDE_OOS_STORAGE_KEY, value ? "1" : "0");
  } catch {
    // ignore
  }
}

export function CatalogPage() {
  const { setCart } = useCart();

  const [data, setData] = useState<CatalogResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [q, setQ] = useState("");
  const [category, setCategory] = useState("");
  const [brand, setBrand] = useState("");
  const [page, setPage] = useState(1);
  const [viewMode, setViewMode] = useState<ViewMode>(() => loadViewMode());
  const [includeOutOfStock, setIncludeOutOfStock] = useState<boolean>(
    () => loadIncludeOutOfStock(),
  );

  const [quantities, setQuantities] = useState<Record<number, number>>({});
  const [adding, setAdding] = useState<Record<number, boolean>>({});
  const [flash, setFlash] = useState<{ type: "ok" | "err"; text: string } | null>(null);
  const [upgradeTarget, setUpgradeTarget] = useState<Plan | null>(null);
  const [popular, setPopular] = useState<CatalogProduct[]>([]);

  const setView = (mode: ViewMode) => {
    setViewMode(mode);
    saveViewMode(mode);
  };

  const load = useCallback(
    async (params: {
      q: string;
      category: string;
      brand: string;
      page: number;
      includeOutOfStock: boolean;
    }) => {
      setLoading(true);
      setError(null);
      try {
        const res = await endpoints.fetchCatalog({
          q: params.q || undefined,
          category: params.category || undefined,
          brand: params.brand || undefined,
          page: params.page,
          per_page: 20,
          include_out_of_stock: params.includeOutOfStock || undefined,
        });
        setData(res);
      } catch (e) {
        setError(apiErrorMessage(e, "カタログ取得に失敗しました"));
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  useEffect(() => {
    void load({ q, category, brand, page, includeOutOfStock });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  // 「人気 TOP 10」は初回 + 発注時に再取得（カート追加直後の最新を反映）
  const loadPopular = useCallback(async () => {
    try {
      const res = await endpoints.fetchPopular(10, 30);
      setPopular(res.products);
    } catch {
      // 人気が取れなくてもカタログ本体は表示する
      setPopular([]);
    }
  }, []);

  useEffect(() => {
    void loadPopular();
  }, [loadPopular]);

  const onSearch = (e: FormEvent) => {
    e.preventDefault();
    setPage(1);
    void load({ q, category, brand, page: 1, includeOutOfStock });
  };

  const onReset = () => {
    setQ("");
    setCategory("");
    setBrand("");
    setPage(1);
    void load({ q: "", category: "", brand: "", page: 1, includeOutOfStock });
  };

  const onToggleIncludeOutOfStock = (value: boolean) => {
    setIncludeOutOfStock(value);
    saveIncludeOutOfStock(value);
    setPage(1);
    void load({ q, category, brand, page: 1, includeOutOfStock: value });
  };

  const handleQtyChange = (productId: number, orderUnit: number, value: number) => {
    const safe = Math.max(orderUnit, value);
    setQuantities((prev) => ({ ...prev, [productId]: safe - (safe % orderUnit) }));
  };

  const onAdd = async (p: CatalogProduct) => {
    if (!p.accessible) return;
    const qty = quantities[p.id] ?? p.order_unit;
    setAdding((prev) => ({ ...prev, [p.id]: true }));
    setFlash(null);
    try {
      const res = await endpoints.addToCart(p.id, qty);
      setCart(res);
      setFlash({ type: "ok", text: `「${p.product_name}」を ${qty} 個カートに追加しました` });
    } catch (e) {
      setFlash({ type: "err", text: apiErrorMessage(e, "カート追加に失敗しました") });
    } finally {
      setAdding((prev) => ({ ...prev, [p.id]: false }));
    }
  };

  const totalPages = data?.pagination.total_pages ?? 0;
  const products = useMemo(() => data?.products ?? [], [data]);

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold">カタログ</h1>
          <p className="text-sm text-gray-500 mt-1">
            商品を検索してカートに追加できます。プランで購入できない商品は🔒で表示されます。
          </p>
        </div>
        {CSV_EXPORT_ENABLED && (
          <button
            type="button"
            className="btn-secondary text-sm"
            onClick={async () => {
              try {
                await endpoints.downloadCatalogCsv(includeOutOfStock);
              } catch (e) {
                setFlash({ type: "err", text: apiErrorMessage(e, "CSV ダウンロードに失敗しました") });
              }
            }}
            title="JAN・商品名・税込価格を CSV でダウンロード"
          >
            📥 商品リスト CSV
          </button>
        )}
      </div>

      {popular.length > 0 && (
        <PopularSection
          products={popular}
          adding={adding}
          onAdd={onAdd}
        />
      )}

      <form onSubmit={onSearch} className="card p-4 grid gap-3 md:grid-cols-4">
        <div className="md:col-span-2">
          <label className="label" htmlFor="q">
            キーワード（商品名・JAN）
          </label>
          <input
            id="q"
            className="input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="例: アリミノ / 4901234567001"
          />
        </div>
        <div>
          <label className="label" htmlFor="category">
            カテゴリ
          </label>
          <input
            id="category"
            className="input"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
          />
        </div>
        <div>
          <label className="label" htmlFor="brand">
            ブランド
          </label>
          <input
            id="brand"
            className="input"
            value={brand}
            onChange={(e) => setBrand(e.target.value)}
          />
        </div>
        <div className="md:col-span-4 flex gap-2 justify-end">
          <button type="button" className="btn-secondary" onClick={onReset}>
            リセット
          </button>
          <button type="submit" className="btn-primary">
            検索
          </button>
        </div>
      </form>

      {flash && (
        <div
          className={`rounded-md px-4 py-3 text-sm border ${
            flash.type === "ok"
              ? "bg-green-50 border-green-200 text-green-800"
              : "bg-red-50 border-red-200 text-red-800"
          }`}
        >
          {flash.text}
        </div>
      )}

      {error && (
        <div className="rounded-md bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-800">
          {error}
        </div>
      )}

      {/* 件数と表示切替 */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="text-sm text-gray-600">
          {data
            ? `全 ${data.pagination.total} 件${
                data.pagination.total > 0
                  ? `（${(page - 1) * data.pagination.per_page + 1}–${Math.min(
                      page * data.pagination.per_page,
                      data.pagination.total,
                    )} 件目を表示）`
                  : ""
              }`
            : ""}
        </div>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-1.5 text-sm text-gray-700 select-none cursor-pointer">
            <input
              type="checkbox"
              className="h-4 w-4 rounded border-gray-300 text-brand-600 focus:ring-brand-500"
              checked={includeOutOfStock}
              onChange={(e) => onToggleIncludeOutOfStock(e.target.checked)}
            />
            在庫切れも表示
          </label>
        <div
          className="inline-flex rounded-md border border-gray-300 overflow-hidden text-sm"
          role="group"
          aria-label="表示切替"
        >
          <button
            type="button"
            className={`px-3 py-1.5 ${
              viewMode === "grid"
                ? "bg-brand-600 text-white"
                : "bg-white text-gray-700 hover:bg-gray-50"
            }`}
            onClick={() => setView("grid")}
            aria-pressed={viewMode === "grid"}
            title="格子状に画像付きで表示"
          >
            ▦ グリッド
          </button>
          <button
            type="button"
            className={`px-3 py-1.5 border-l border-gray-300 ${
              viewMode === "list"
                ? "bg-brand-600 text-white"
                : "bg-white text-gray-700 hover:bg-gray-50"
            }`}
            onClick={() => setView("list")}
            aria-pressed={viewMode === "list"}
            title="縦に並べて一覧表示"
          >
            ☰ リスト
          </button>
        </div>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-12 text-gray-500">読み込み中...</div>
      ) : products.length === 0 ? (
        <div className="text-center py-12 text-gray-500">該当する商品がありません。</div>
      ) : viewMode === "grid" ? (
        <GridView
          products={products}
          quantities={quantities}
          adding={adding}
          onQty={handleQtyChange}
          onAdd={onAdd}
          onUpgradeClick={setUpgradeTarget}
        />
      ) : (
        <ListView
          products={products}
          quantities={quantities}
          adding={adding}
          onQty={handleQtyChange}
          onAdd={onAdd}
          onUpgradeClick={setUpgradeTarget}
        />
      )}

      {upgradeTarget && (
        <PlanChangeDialog
          initialPlan={upgradeTarget}
          onClose={() => setUpgradeTarget(null)}
          onChanged={(res) => {
            setFlash({
              type: "ok",
              text: `プランを ${res.plan.toUpperCase()} にアップグレードしました。該当商品が購入可能になりました。`,
            });
            // カタログを再取得してロックを解除
            void load({ q, category, brand, page, includeOutOfStock });
          }}
        />
      )}

      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 pt-4">
          <button
            type="button"
            className="btn-secondary"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page <= 1}
          >
            前へ
          </button>
          <div className="text-sm text-gray-600">
            {page} / {totalPages} ページ
          </div>
          <button
            type="button"
            className="btn-secondary"
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page >= totalPages}
          >
            次へ
          </button>
        </div>
      )}
    </div>
  );
}

// ============================================================
// ビュー実装
// ============================================================

interface ViewProps {
  products: CatalogProduct[];
  quantities: Record<number, number>;
  adding: Record<number, boolean>;
  onQty: (productId: number, orderUnit: number, value: number) => void;
  onAdd: (p: CatalogProduct) => void;
  onUpgradeClick: (requiredPlan: Plan) => void;
}

function GridView({ products, quantities, adding, onQty, onAdd, onUpgradeClick }: ViewProps) {
  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
      {products.map((p) =>
        p.accessible ? (
          <div key={p.id} className="card p-4 flex flex-col">
            {p.image_url ? (
              <img
                src={p.image_url}
                alt={p.product_name}
                className="w-full aspect-square object-cover rounded mb-3 bg-gray-100"
              />
            ) : (
              <div className="w-full aspect-square bg-gray-100 rounded mb-3 flex items-center justify-center text-gray-400 text-sm">
                画像なし
              </div>
            )}
            <div className="flex-1 space-y-2">
              <div>
                <h3 className="font-semibold line-clamp-2">{p.product_name}</h3>
                <p className="text-xs text-gray-500 mt-1">{p.jan}</p>
              </div>
              {Object.keys(p.fields).length > 0 && (
                <dl className="text-xs text-gray-600 space-y-0.5">
                  {Object.entries(p.fields).map(([k, v]) => (
                    <div key={k}>
                      <span className="font-medium">{k}:</span> {v}
                    </div>
                  ))}
                </dl>
              )}
              <div className="flex items-center justify-between">
                <div className="text-lg font-bold">
                  {formatYen(p.tax_inclusive_price)}
                  <span className="text-xs font-normal text-gray-500 ml-1">(税込)</span>
                </div>
                <StockBadge status={p.stock_status} count={p.public_stock} />
              </div>
              <div className="text-xs text-gray-500">発注単位: {p.order_unit} 個</div>
            </div>
            <div className="mt-3 pt-3 border-t flex items-center gap-2">
              <button
                type="button"
                className="btn-secondary px-2 py-1"
                disabled={p.stock_status === "out_of_stock"}
                onClick={() =>
                  onQty(
                    p.id,
                    p.order_unit,
                    (quantities[p.id] ?? p.order_unit) - p.order_unit,
                  )
                }
              >
                −
              </button>
              <input
                type="number"
                className="input w-20 text-center"
                min={p.order_unit}
                step={p.order_unit}
                value={quantities[p.id] ?? p.order_unit}
                onChange={(e) => onQty(p.id, p.order_unit, Number(e.target.value))}
                disabled={p.stock_status === "out_of_stock"}
              />
              <button
                type="button"
                className="btn-secondary px-2 py-1"
                disabled={p.stock_status === "out_of_stock"}
                onClick={() =>
                  onQty(
                    p.id,
                    p.order_unit,
                    (quantities[p.id] ?? p.order_unit) + p.order_unit,
                  )
                }
              >
                ＋
              </button>
              <button
                type="button"
                className="btn-primary flex-1"
                onClick={() => onAdd(p)}
                disabled={adding[p.id] || p.stock_status === "out_of_stock"}
              >
                {adding[p.id] ? "追加中..." : "カートに追加"}
              </button>
            </div>
          </div>
        ) : (
          <LockedGridCard
            key={p.id}
            product={p}
            onUpgradeClick={() => onUpgradeClick(p.required_plan)}
          />
        ),
      )}
    </div>
  );
}

function LockedGridCard({
  product,
  onUpgradeClick,
}: {
  product: CatalogProduct & { accessible: false };
  onUpgradeClick: () => void;
}) {
  return (
    <div className="card p-4 bg-gray-50 border-dashed relative flex flex-col">
      <div className="aspect-square bg-gray-200 rounded mb-3 flex items-center justify-center text-4xl">
        🔒
      </div>
      <h3 className="font-semibold line-clamp-2 text-gray-700">{product.product_name}</h3>
      <p className="text-xs text-gray-500 mt-1">{product.jan}</p>
      <div className="mt-3 text-sm text-brand-700 font-medium flex-1">
        {PLAN_LABEL[product.required_plan]} プランで購入可能
      </div>
      <button
        type="button"
        className="btn-primary w-full mt-3"
        onClick={onUpgradeClick}
      >
        {PLAN_LABEL[product.required_plan]} にアップグレード
      </button>
    </div>
  );
}

/**
 * リスト表示（発注履歴のような縦並びの 1 行 1 商品）。
 * 画像は小さめサムネ、商品名・JAN・価格・在庫・数量ステッパー・追加ボタンを横一列に配置。
 */
function ListView({ products, quantities, adding, onQty, onAdd, onUpgradeClick }: ViewProps) {
  return (
    <div className="card overflow-hidden">
      <ul className="divide-y divide-gray-200">
        {products.map((p) => (
          <li key={p.id} className={p.accessible ? "" : "bg-gray-50"}>
            {p.accessible ? (
              <div className="p-3 flex gap-4 items-center">
                {/* サムネ */}
                {p.image_url ? (
                  <img
                    src={p.image_url}
                    alt={p.product_name}
                    className="w-16 h-16 rounded object-cover bg-gray-100 flex-shrink-0"
                  />
                ) : (
                  <div className="w-16 h-16 rounded bg-gray-100 flex items-center justify-center text-gray-400 text-[10px] flex-shrink-0">
                    画像なし
                  </div>
                )}
                {/* 情報 */}
                <div className="flex-1 min-w-0">
                  <div className="font-mono text-xs text-gray-500">{p.jan}</div>
                  <div className="font-semibold truncate">{p.product_name}</div>
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1 text-xs text-gray-600">
                    <span className="text-base font-bold text-gray-900">
                      {formatYen(p.tax_inclusive_price)}
                      <span className="text-[10px] font-normal text-gray-500 ml-1">(税込)</span>
                    </span>
                    <StockBadge status={p.stock_status} count={p.public_stock} />
                    <span>発注単位 {p.order_unit} 個</span>
                    {Object.entries(p.fields).map(([k, v]) => (
                      <span key={k} className="text-gray-500">
                        {k}: {v}
                      </span>
                    ))}
                  </div>
                </div>
                {/* 数量＋追加 */}
                <div className="flex items-center gap-1 flex-shrink-0">
                  <button
                    type="button"
                    className="btn-secondary px-2 py-1"
                    disabled={p.stock_status === "out_of_stock"}
                    onClick={() =>
                      onQty(
                        p.id,
                        p.order_unit,
                        (quantities[p.id] ?? p.order_unit) - p.order_unit,
                      )
                    }
                  >
                    −
                  </button>
                  <input
                    type="number"
                    className="input w-20 text-center"
                    min={p.order_unit}
                    step={p.order_unit}
                    value={quantities[p.id] ?? p.order_unit}
                    onChange={(e) => onQty(p.id, p.order_unit, Number(e.target.value))}
                    disabled={p.stock_status === "out_of_stock"}
                  />
                  <button
                    type="button"
                    className="btn-secondary px-2 py-1"
                    disabled={p.stock_status === "out_of_stock"}
                    onClick={() =>
                      onQty(
                        p.id,
                        p.order_unit,
                        (quantities[p.id] ?? p.order_unit) + p.order_unit,
                      )
                    }
                  >
                    ＋
                  </button>
                  <button
                    type="button"
                    className="btn-primary ml-2"
                    onClick={() => onAdd(p)}
                    disabled={adding[p.id] || p.stock_status === "out_of_stock"}
                  >
                    {adding[p.id] ? "追加中..." : "カートに追加"}
                  </button>
                </div>
              </div>
            ) : (
              <LockedListRow
                product={p}
                onUpgradeClick={() => onUpgradeClick(p.required_plan)}
              />
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

function LockedListRow({
  product,
  onUpgradeClick,
}: {
  product: CatalogProduct & { accessible: false };
  onUpgradeClick: () => void;
}) {
  return (
    <div className="p-3 flex gap-4 items-center">
      <div className="w-16 h-16 rounded bg-gray-200 flex items-center justify-center text-2xl flex-shrink-0">
        🔒
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-mono text-xs text-gray-500">{product.jan}</div>
        <div className="font-semibold truncate text-gray-700">{product.product_name}</div>
        <div className="text-sm text-brand-700 font-medium mt-0.5">
          {PLAN_LABEL[product.required_plan]} プランで購入可能
        </div>
      </div>
      <button
        type="button"
        className="btn-primary flex-shrink-0"
        onClick={onUpgradeClick}
      >
        アップグレード
      </button>
    </div>
  );
}

// ============================================================
// 人気 TOP（直近 30 日）
// ============================================================

function PopularSection({
  products,
  adding,
  onAdd,
}: {
  products: CatalogProduct[];
  adding: Record<number, boolean>;
  onAdd: (p: CatalogProduct) => void;
}) {
  return (
    <section className="card p-4">
      <div className="flex items-baseline justify-between mb-3">
        <h2 className="text-lg font-semibold">🔥 人気 TOP {products.length}</h2>
        <span className="text-xs text-gray-500">直近 30 日間の発注数</span>
      </div>
      <div className="flex gap-3 overflow-x-auto pb-2 -mx-1 px-1">
        {products.map((p, idx) =>
          p.accessible ? (
            <div
              key={p.id}
              className="flex-shrink-0 w-44 border border-gray-200 rounded-md p-3 flex flex-col gap-2 bg-white"
            >
              <div className="relative">
                {p.image_url ? (
                  <img
                    src={p.image_url}
                    alt={p.product_name}
                    className="w-full aspect-square object-cover rounded bg-gray-100"
                  />
                ) : (
                  <div className="w-full aspect-square bg-gray-100 rounded flex items-center justify-center text-gray-400 text-xs">
                    画像なし
                  </div>
                )}
                <span className="absolute top-1 left-1 bg-brand-600 text-white text-xs font-semibold px-1.5 py-0.5 rounded">
                  {idx + 1}
                </span>
              </div>
              <h3 className="text-sm font-medium line-clamp-2 leading-tight" title={p.product_name}>
                {p.product_name}
              </h3>
              <div className="text-base font-bold">
                {formatYen(p.tax_inclusive_price)}
                <span className="text-xs font-normal text-gray-500 ml-1">(税込)</span>
              </div>
              <button
                type="button"
                className="btn-primary text-xs py-1.5"
                disabled={adding[p.id] || p.stock_status === "out_of_stock"}
                onClick={() => onAdd(p)}
              >
                {p.stock_status === "out_of_stock"
                  ? "在庫切れ"
                  : adding[p.id]
                    ? "追加中..."
                    : `${p.order_unit} 個 カート追加`}
              </button>
            </div>
          ) : null,
        )}
      </div>
    </section>
  );
}
